import { produce } from 'immer';
import { create } from 'zustand';

import { EPropertySettingTypes } from 'api/meta/enums/nodes/properties/EPropertySettingTypes';
import type { EventHistoryInfo } from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistory.types';
import type { HistoryState, ProjectHistory } from 'state/history/historyState';

export const HISTORY_STORE = create<HistoryState>((set) => ({
  history: {},
  eventsHistory: {},

  selectedNodeProperty: undefined,

  selectedTemplateNodeId: undefined,
  selectedTargetPropertyName: '',
  selectedTargetPropertyType: EPropertySettingTypes.Text,

  isFetching: false,
  setIsFetching: (value: boolean) => {
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.isFetching = value;
      });

      return newState;
    });
  },

  setSelectedNodeProperty: (
    projectId: string,
    nodeId: string,
    propertyId: string,
    templateNodeId,
    targetPropertyName,
    targetPropertyType
  ) => {
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.selectedNodeProperty = `${projectId}_${nodeId}_${propertyId}`;
        draft.selectedTemplateNodeId = templateNodeId;
        draft.selectedTargetPropertyName = targetPropertyName;
        draft.selectedTargetPropertyType = targetPropertyType;
      });

      return newState;
    });
  },

  initialize: (history: { [p: string]: ProjectHistory }) =>
    set((state) => {
      return produce(state, (draft) => {
        draft.history = history;
      });
    }),

  create: (history: ProjectHistory) =>
    set((state) => {
      return produce(state, (draft) => {
        if (draft.history === undefined || history.projectId === undefined) {
          return;
        }

        if (draft.history[history.projectId] === undefined) {
          draft.history[history.projectId] = history;
        }
      });
    }),

  remove: (projectId: string) =>
    set((state) => {
      return produce(state, (draft) => {
        if (draft.history === undefined || draft.history[projectId] === undefined) {
          return;
        }

        delete draft.history[projectId];
      });
    }),

  update: (projectId: string, history: ProjectHistory) =>
    set((state) => {
      return produce(state, (draft) => {
        if (draft.history === undefined || draft.history[projectId] === undefined) {
          return;
        }

        draft.history[projectId] = history;
      });
    }),

  open: (projectId: string) =>
    set((state) => {
      return produce(state, (draft) => {
        if (draft.history === undefined || draft.history[projectId] === undefined) {
          return;
        }

        if (!draft.history[projectId].open) {
          draft.history[projectId].open = true;
        }
      });
    }),

  close: (projectId: string) =>
    set((state) => {
      return produce(state, (draft) => {
        if (draft.history === undefined || draft.history[projectId] === undefined) {
          return;
        }

        if (draft.history[projectId].open) {
          draft.history[projectId].open = false;
        }
      });
    }),

  toggle: (projectId: string) =>
    set((state) => {
      return produce(state, (draft) => {
        if (draft.history === undefined || draft.history[projectId] === undefined) {
          return;
        }

        draft.history[projectId].open = !draft.history[projectId].open;
      });
    }),

  setEventsHistory: (projectId: string, nodeId: string, propertyId: string, eventsHistory: EventHistoryInfo[]) =>
    set((state) => {
      return produce(state, (draft) => {
        const keyEventHistory = `${projectId}_${nodeId}_${propertyId}`;
        draft.eventsHistory[keyEventHistory] = eventsHistory;
      });
    })
}));
